// src/components/atomic-crm/contacts/ContactShow.tsx
// src/components/atomic-crm/contacts/ContactShow.tsx
import {
  ShowBase,
  useListContext,
  useRecordContext,
  useShowContext,
  useTranslate,
  useNotify,
  useRedirect,
} from "ra-core";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import { Button } from "@/components/ds/ui/button";
import { ReferenceField } from "@/components/ds/admin/reference-field";
import { ReferenceManyField } from "@/components/ds/admin/reference-many-field";
import { TextField } from "@/components/ds/admin/text-field";
import { Card, CardContent } from "@/components/ds/ui/card";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { Avatar } from "./Avatar";
import { ActivityStream } from "../activities/ActivityStream";
import { InvoiceCard } from "../invoices";
import type { Contact, Invoice } from "../types";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { ProfileCard } from "@/components/atomic-crm/profile/ProfileCard";
import { ContactAside, ContactAsideActions } from "./ContactAside";
// isCanceledRequestError — unchanged

export const ContactShow = () => (
  <ShowBase redirectOnError={false} queryOptions={{ onError: () => undefined }}>
    <ContactShowContent />
  </ShowBase>
);

// Helper function to check for canceled requests
const isCanceledRequestError = (value: unknown) => {
  if (!value) return false;

  const pending: unknown[] = [value];
  const seen = new Set<unknown>();

  while (pending.length > 0) {
    const current = pending.pop();
    if (!current || seen.has(current)) continue;
    seen.add(current);

    if (typeof current === "string") {
      const text = current.toLowerCase();
      if (
        text.includes("abort") ||
        text.includes("canceled") ||
        text.includes("cancelled") ||
        text.includes("err_canceled")
      ) {
        return true;
      }
      continue;
    }

    if (typeof current !== "object") continue;

    const maybeError = current as {
      name?: unknown;
      message?: unknown;
      code?: unknown;
      details?: unknown;
      cause?: unknown;
      error?: unknown;
      reason?: unknown;
    };

    const text = [
      maybeError.name,
      maybeError.message,
      maybeError.code,
      maybeError.details,
    ]
      .filter((token): token is string => typeof token === "string")
      .map((token) => token.toLowerCase())
      .join(" ");

    if (
      text.includes("abort") ||
      text.includes("canceled") ||
      text.includes("cancelled") ||
      text.includes("err_canceled")
    ) {
      return true;
    }

    if (maybeError.cause) pending.push(maybeError.cause);
    if (maybeError.error) pending.push(maybeError.error);
    if (maybeError.reason) pending.push(maybeError.reason);
  }

  return false;
};

const ContactShowContent = () => {
  const { record, isPending, error } = useShowContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const contactId = record?.id;

  const [refreshKey, setRefreshKey] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: profileRow, refetch: refetchProfile } = useQuery({
    queryKey: ["ai_profiles", "contact", contactId, refreshKey],
    queryFn: async () => {
      if (!contactId) return null;
      const { data, error } = await supabase
        .from("ai_profiles")
        .select("profile, generated_at, model, regenerated_count")
        .eq("entity_type", "contact")
        .eq("entity_id", contactId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!contactId,
  });

  const generateProfile = async (force: boolean) => {
    if (!contactId) return;
    setIsGenerating(true);
    try {
      await supabase.functions.invoke("generate-profile", {
        body: { entity_type: "contact", entity_id: contactId, force },
      });
      refetchProfile();
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (isPending || record || !error || isCanceledRequestError(error)) return;
    notify("Contact not found or has been deleted", { type: "error" });
    redirect("/contacts");
  }, [error, isPending, notify, record, redirect]);

  if (isPending || !record) return null;

  return (
    <div className="mt-2 mb-2 flex gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <Avatar />
              <div className="flex-1 min-w-0">
                <h5 className="text-lg sm:text-xl font-semibold truncate">
                  {record.first_name} {record.last_name}
                </h5>
                <div className="text-sm text-muted-foreground truncate">
                  {record.title}
                  {record.title &&
                    record.company_id != null &&
                    ` ${translate("crm.contact.field.at")} `}
                  {record.company_id != null && (
                    <ReferenceField
                      source="company_id"
                      reference="companies"
                      link="show"
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  )}
                </div>
              </div>
              {/* Company avatar on the right (desktop) */}
              <div className="hidden sm:block">
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                  className="no-underline"
                >
                  <CompanyAvatar />
                </ReferenceField>
              </div>
              {/* Actions on mobile */}
              <div className="sm:hidden">
                <ContactAsideActions />
              </div>
            </div>

            {/* ✅  Profile Section with Card */}
            <div className="mt-6">
              <ProfileCard
                profileRow={profileRow}
                variant="contact"
                isGenerating={isGenerating}
                onGenerate={() => generateProfile(true)}
                onRefresh={() => generateProfile(true)}
              />
            </div>

            {/* Activity Timeline */}
            {/* Activity Timeline */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">
                {translate("crm.contact.section.activity_timeline")}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {translate("crm.contact.section.activity_timeline_description")}
              </p>
              <ActivityStream
                resource="contacts"
                recordId={record.id}
                record={record}
              />
            </div>

            {/* Invoices Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">
                {translate("resources.invoices.name", { smart_count: 2 })}
              </h3>
              <ReferenceManyField
                target="contact_id"
                reference="invoices"
                sort={{ field: "issue_date", order: "DESC" }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex justify-end">
                    <CreateRelatedInvoiceButton />
                  </div>
                  <InvoicesIterator />
                </div>
              </ReferenceManyField>
            </div>
          </CardContent>
        </Card>
      </div>
      <ContactAside />
    </div>
  );
};

const InvoicesIterator = () => {
  const { data: invoices, error, isPending } = useListContext<Invoice>();
  if (isPending || error) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {invoices.map((invoice) => (
        <RouterLink key={invoice.id} to={`/invoices/${invoice.id}/show`}>
          <InvoiceCard invoice={invoice} />
        </RouterLink>
      ))}
    </div>
  );
};

const CreateRelatedInvoiceButton = () => {
  const contact = useRecordContext<Contact>();
  const translate = useTranslate();
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/invoices/create"
        state={
          contact
            ? {
              record: {
                contact_id: contact.id,
                company_id: contact.company_id,
              },
            }
            : undefined
        }
        className="flex items-center gap-2"
      >
        <Receipt className="h-4 w-4" />
        {translate("crm.common.add_invoice")}
      </RouterLink>
    </Button>
  );
};